/**
 *
 * WmsSignIn
 *
 * Standalone handheld sign-in page for the new WMS app. Mirrors the
 * warehouse-facing layout from shiphawk/wms/app/views/auth/sessions/new.html.erb:
 * full-viewport flex column, ShipHawk logo + "WAREHOUSE MANAGEMENT" header,
 * two-step email/password flow centered in the remaining space, no forgot
 * password / help modal / footer.
 *
 * Posts `target_app=wms2` to the tenants authenticate endpoints so the
 * backend routes post-auth to the tenant's wms2 instance.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import logo from '../../images/shiphawk-logo-navy-retina.png';
import { ReactComponent as EmailIcon } from '../../svg/email.svg';
import { ReactComponent as PasswordIcon } from '../../svg/password.svg';
import { login, fetchAuthFlow } from '../SignIn/actions.js';
import { validateEmail } from '../helpers.js';
import styles from './styles.module.css';

const TARGET_APP = 'wms2';
const HELP_PAGE_URL = 'https://shiphawk.atlassian.net/l/cp/KWdfvRGi';

function WmsSignIn() {
  const location = useLocation();
  const [authStep, setAuthStep] = useState('email');
  const [userEmail, setUserEmail] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [backendError, setBackendError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOtpRequired, setIsOtpRequired] = useState(false);
  const [otpDetails, setOtpDetails] = useState({});
  const [otpInput, setOtpInput] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isQrCodeShown, setIsQrCodeShown] = useState(false);
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const otpInputRef = useRef(null);

  const { requiredAction, otpSecret, otpProvisioningUri } = otpDetails;
  const showRequiredAction = isOtpRequired && !!requiredAction;
  const showOtpSetup = isOtpRequired && !requiredAction && !!otpSecret;
  const showOtpCodeOnly = isOtpRequired && !requiredAction && !otpSecret;
  const showOtpForm = showOtpCodeOnly || (showOtpSetup && isQrCodeShown);

  useEffect(() => {
    const urlError = new URLSearchParams(location.search).get('error');
    if (urlError) {
      setBackendError(decodeURIComponent(urlError));
    }
  }, [location.search]);

  useEffect(() => {
    if (showOtpForm) {
      otpInputRef.current?.focus();
      return;
    }
    if (authStep === 'email') {
      emailInputRef.current?.focus();
    } else if (authStep === 'password' && !isOtpRequired) {
      passwordInputRef.current?.focus();
    }
  }, [authStep, isOtpRequired, showOtpForm]);

  const setAuthFlow = useCallback((data) => {
    if (!data) return;
    if (data.flow === 'saml' && data.sso_url) {
      window.location.href = data.sso_url;
    } else if (data.flow === 'password') {
      setAuthStep('password');
    }
  }, []);

  const handleEmailSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      const email = emailInput.trim();
      if (!email || !validateEmail(email)) {
        setEmailError('Please enter a valid email address');
        return;
      }
      setEmailError('');
      setBackendError('');
      setUserEmail(email);
      setIsLoading(true);
      try {
        await fetchAuthFlow({
          email,
          setError: setBackendError,
          setAuthFlow,
          targetApp: TARGET_APP,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [emailInput, setAuthFlow],
  );

  const handlePasswordSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      const password = passwordInput.trim();
      if (!password) {
        setPasswordError('Please enter a password');
        return;
      }
      setPasswordError('');
      setBackendError('');
      setIsLoading(true);
      try {
        await login({
          email: userEmail,
          password,
          setError: setBackendError,
          setIsOtpRequired,
          setOtpDetails,
          targetApp: TARGET_APP,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [passwordInput, userEmail],
  );

  const handleOtpSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      const otp = otpInput.trim();
      if (!otp) {
        setOtpError(
          'Please enter verification code from your authenticator app',
        );
        return;
      }
      setOtpError('');
      setBackendError('');
      setIsLoading(true);
      try {
        await login({
          email: otpDetails.email,
          password: otpDetails.password,
          otp,
          setError: setBackendError,
          setIsOtpRequired,
          setOtpDetails,
          targetApp: TARGET_APP,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [otpInput, otpDetails],
  );

  const handleShowQrCode = useCallback(() => setIsQrCodeShown(true), []);

  const handleBackToEmail = useCallback(() => {
    setAuthStep('email');
    setPasswordInput('');
    setPasswordError('');
    setBackendError('');
    setIsOtpRequired(false);
    setOtpDetails({});
    setOtpInput('');
    setOtpError('');
    setIsQrCodeShown(false);
  }, []);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <img className={styles.logo} src={logo} alt="ShipHawk" />
        <span className={styles.subtitle}>Warehouse Management</span>
      </header>

      <main className={styles.main}>
        <div className={styles.card}>
          {backendError && <div className={styles.alert}>{backendError}</div>}

          {authStep === 'email' && (
            <form onSubmit={handleEmailSubmit} noValidate>
              <div className={styles.headingBlock}>
                <h1 className={styles.heading}>Welcome Back</h1>
                <p className={styles.caption}>
                  Enter your email address to continue
                </p>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="wms-email">
                  Email<span className={styles.required}>*</span>
                </label>
                <div className={styles.inputWrapper}>
                  <EmailIcon className={styles.inputIcon} />
                  <input
                    ref={emailInputRef}
                    id="wms-email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    placeholder="your.email@company.com"
                    className={`${styles.input} ${
                      emailError ? styles.inputError : ''
                    }`}
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                  />
                </div>
                {emailError && (
                  <div className={styles.errorText}>{emailError}</div>
                )}
              </div>

              <button
                type="submit"
                className={styles.button}
                disabled={isLoading}
              >
                {isLoading ? 'Checking…' : 'Continue'}
              </button>
            </form>
          )}

          {authStep === 'password' && showRequiredAction && (
            <>
              <div className={styles.warning}>{requiredAction}</div>
              <button
                type="button"
                className={styles.backLink}
                onClick={handleBackToEmail}
              >
                ← Use a different email
              </button>
            </>
          )}

          {authStep === 'password' && showOtpSetup && !isQrCodeShown && (
            <>
              <div className={styles.headingBlock}>
                <h1 className={styles.heading}>Set up 2FA</h1>
                <p className={styles.caption}>
                  Your ShipHawk Administrator has enabled Two-Factor
                  Authentication (2FA) for this account.
                </p>
              </div>
              <button
                type="button"
                className={styles.button}
                onClick={handleShowQrCode}
              >
                Generate code for 2FA
              </button>
              <button
                type="button"
                className={styles.backLink}
                onClick={handleBackToEmail}
              >
                ← Use a different email
              </button>
            </>
          )}

          {authStep === 'password' && showOtpForm && (
            <form onSubmit={handleOtpSubmit} noValidate>
              <div className={styles.headingBlock}>
                <h1 className={styles.heading}>
                  {showOtpSetup ? 'Scan QR code' : 'Verification code'}
                </h1>
                <p className={styles.caption}>
                  {showOtpSetup
                    ? 'Scan with your authenticator app, then enter the 6-digit code.'
                    : 'Enter the 6-digit code from your authenticator app.'}
                </p>
              </div>

              {showOtpSetup && (
                <div className={styles.qrCode}>
                  <QRCodeSVG level="M" size={160} value={otpProvisioningUri} />
                  <p className={styles.otpSecret}>{otpSecret}</p>
                </div>
              )}

              <div className={styles.field}>
                <label className={styles.label} htmlFor="wms-otp">
                  Verification Code
                  <span className={styles.required}>*</span>
                </label>
                <div className={styles.inputWrapper}>
                  <PasswordIcon className={styles.inputIcon} />
                  <input
                    ref={otpInputRef}
                    id="wms-otp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    name="otp"
                    placeholder="123456"
                    className={`${styles.input} ${
                      otpError ? styles.inputError : ''
                    }`}
                    value={otpInput}
                    onChange={(e) => setOtpInput(e.target.value)}
                  />
                </div>
                {otpError && <div className={styles.errorText}>{otpError}</div>}
              </div>

              <button
                type="submit"
                className={styles.button}
                disabled={isLoading}
              >
                {isLoading ? 'Verifying…' : 'Log In'}
              </button>

              {showOtpSetup && (
                <a
                  className={styles.helpLink}
                  href={HELP_PAGE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more about 2FA setup
                </a>
              )}

              <button
                type="button"
                className={styles.backLink}
                onClick={handleBackToEmail}
              >
                ← Use a different email
              </button>
            </form>
          )}

          {authStep === 'password' && !isOtpRequired && (
            <form onSubmit={handlePasswordSubmit} noValidate>
              <div className={styles.headingBlock}>
                <h1 className={styles.heading}>Enter Password</h1>
                <p className={styles.caption}>{userEmail}</p>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="wms-password">
                  Password<span className={styles.required}>*</span>
                </label>
                <div className={styles.inputWrapper}>
                  <PasswordIcon className={styles.inputIcon} />
                  <input
                    ref={passwordInputRef}
                    id="wms-password"
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className={`${styles.input} ${
                      passwordError ? styles.inputError : ''
                    }`}
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                  />
                </div>
                {passwordError && (
                  <div className={styles.errorText}>{passwordError}</div>
                )}
              </div>

              <button
                type="submit"
                className={styles.button}
                disabled={isLoading}
              >
                {isLoading ? 'Signing in…' : 'Sign In'}
              </button>

              <button
                type="button"
                className={styles.backLink}
                onClick={handleBackToEmail}
              >
                ← Use a different email
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}

export default WmsSignIn;
